import { analyticsTrafficProperties } from "@tabbio/utils/analytics/browser-cookies";
import { browserMeasurementCookies } from "./browser-measurement.web";
import {
  analyticsPageContext,
  analyticsPageUrl,
  isPrivateAnalyticsPath,
  sanitizeAnalyticsProperties,
} from "@tabbio/utils/analytics/privacy";
import {
  ga4ConsentDefault,
  ga4ConsentSignals,
  resolveGa4Destination,
  toGa4Params,
  type Ga4ParamValue,
} from "@tabbio/utils/analytics/ga4";
import {
  buildDataLayerPushes,
  buildUserPushes,
  gtmLibraryUrl,
} from "@tabbio/utils/analytics/gtm";
import {
  isMarketingEnabled,
  isProductAnalyticsEnabled,
  subscribePrivacyPreferences,
} from "./privacy-preferences";

/**
 * GA4 for app.tabbio.com, the second half of the acquisition funnel.
 *
 * The marketing site (tabbio.com) and this app are separate builds, but they
 * report to the SAME measurement ID on purpose. GA4 scopes its client id to the
 * registrable domain, so a visitor who lands on www.tabbio.com from a campaign
 * and signs up here keeps one client id and one session, which is the only
 * reason `signup_completed` can be attributed back to the campaign that caused
 * it. Two properties, or two measurement IDs, would split that visitor in half
 * and report every signup as `(direct)`.
 *
 * The Tag Manager container is injected from here rather than from
 * `src/app/+html.tsx`. That file
 * is only rendered by Expo's static web export, so a tag placed there is absent
 * from `expo start --web` and from any future switch of `web.output`, and the
 * app would queue events that nothing ever consumes. Injecting at runtime gives
 * dev and production one code path, and costs nothing on a shell that cannot
 * paint before its JS bundle runs anyway.
 */

/**
 * The same Tag Manager container as www.tabbio.com. GA4 (`G-97E51FQ4PY`) is
 * configured inside it, so the measurement id no longer appears in code.
 */
export const GTM_CONTAINER_ID =
  process.env.EXPO_PUBLIC_GTM_CONTAINER_ID?.trim() || "";

/** Distinguishes an in-app web conversion from a marketing-site one. */
export const GA4_SURFACE = "app_web" as const;

type GtagWindow = typeof window & { dataLayer?: unknown[] };

export function isGa4Enabled() {
  return (
    GTM_CONTAINER_ID.length > 0 &&
    typeof window !== "undefined" &&
    !isPrivateAnalyticsPath(window.location.href)
  );
}

function dataLayer(): unknown[] {
  const w = window as GtagWindow;
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
}

/**
 * Queues a gtag-style command (consent, set). GTM reads these from the shared
 * `dataLayer` exactly as gtag.js did, and they have to be real `arguments`
 * objects, which is why this is a function statement rather than an arrow with
 * rest parameters. Pushing before the container has loaded is fine: it replays
 * the queue on initialisation.
 */
function gtag() {
  if (typeof window === "undefined") return;
  dataLayer().push(arguments);
}
const gtagCommand = gtag as (...args: unknown[]) => void;

let initialized = false;

/**
 * Seeds `dataLayer` and loads gtag.js, once per document.
 *
 * The three queue entries have to be pushed before the library is appended:
 * gtag.js replays whatever it finds on load, and `consent default` arriving
 * after initialization means the first ping already went out under the implicit
 * grant Consent Mode exists to prevent.
 */
function ensureGa4Initialized() {
  if (initialized || !isGa4Enabled()) return;
  initialized = true;

  // The same sequence the marketing site inlines in its document head: consent
  // defaults first, then the consent-mode settings that used to sit on the GA4
  // config call, then the container's start marker, then the loader.
  gtagCommand("consent", "default", ga4ConsentDefault());
  gtagCommand("set", "url_passthrough", true);
  gtagCommand("set", "ads_data_redaction", true);
  gtagCommand(
    "consent",
    "update",
    ga4ConsentSignals(isProductAnalyticsEnabled(), isMarketingEnabled()),
  );
  dataLayer().push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = gtmLibraryUrl(GTM_CONTAINER_ID);
  document.head.appendChild(script);
}

let consentSubscribed = false;

/**
 * Replays the stored analytics decision, and keeps replaying it whenever the
 * user changes it in settings.
 *
 * Needed on every load, not just at the moment of choosing: Consent Mode's
 * defaults are denied, and privacy preferences hydrate asynchronously from
 * SecureStore, so the first paint always starts from denied even for a user who
 * granted consent months ago.
 */
export function startGa4ConsentSync() {
  if (!isGa4Enabled() || consentSubscribed) return;
  ensureGa4Initialized();
  consentSubscribed = true;

  let previous: string | null = null;
  const send = () => {
    const analytics = isProductAnalyticsEnabled();
    const marketing = isMarketingEnabled();
    const key = `${analytics}:${marketing}`;
    if (previous === key) return;
    previous = key;
    const vendorWindow = window as unknown as {
      __tabbioMarketingGranted?: boolean;
      __tabbioAnalyticsGranted?: boolean;
      ttq?: {
        grantConsent?: () => void;
        revokeConsent?: () => void;
        disableCookie?: () => void;
      };
      fbq?: (...args: unknown[]) => void;
    };
    if (!analytics)
      for (const name of ["_ga", "_ga_97E51FQ4PY"])
        browserMeasurementCookies.writeCookie(name, null, 0);
    if (!marketing)
      for (const name of [
        "_gcl_au",
        "_gcl_aw",
        "_gcl_dc",
        "_fbp",
        "_fbc",
        "_ttp",
        "li_fat_id",
        "li_adsid",
      ])
        browserMeasurementCookies.writeCookie(name, null, 0);
    vendorWindow.__tabbioMarketingGranted = marketing;
    vendorWindow.__tabbioAnalyticsGranted = analytics;
    if (marketing) vendorWindow.ttq?.grantConsent?.();
    else {
      vendorWindow.ttq?.revokeConsent?.();
      vendorWindow.ttq?.disableCookie?.();
    }
    vendorWindow.fbq?.("consent", marketing ? "grant" : "revoke");
    gtagCommand("consent", "update", ga4ConsentSignals(analytics, marketing));
    dataLayer().push({ event: "tabbio_consent_updated" });
    if (marketing) dataLayer().push({ event: "tabbio_marketing_page_view" });
  };

  send();
  subscribePrivacyPreferences(send);
}

/**
 * Forwards one product-analytics event to GA4.
 *
 * Not consent-gated, by design: Consent Mode is the gate. It withholds the
 * analytics identifier while still allowing a cookieless ping, which is what
 * keeps campaign attribution measurable for users who have not answered the
 * privacy sheet. Gating the send as well would discard exactly the acquisition
 * data this integration exists to collect.
 */
export type Ga4QueuedEvent = {
  readonly name: string;
  readonly params: Record<string, Ga4ParamValue>;
};

/**
 * Shapes a product-analytics event for GA4, or returns `null` when it should
 * not be forwarded at all (see `shouldForwardProductEventToGa4`).
 *
 * A builder rather than a sender so the caller decides *when* it goes out: the
 * queue in `product-analytics.web.ts` defers non-critical events to idle time
 * and hands them to `sendGa4Event` in batches. Shaping happens here, at event
 * time, so the payload is a snapshot of the moment rather than of the flush.
 */
export function buildGa4ProductEvent(
  event: string,
  properties?: Record<string, unknown> | null,
): Ga4QueuedEvent | null {
  if (!isGa4Enabled()) return null;
  const destination = resolveGa4Destination(event);
  if (!destination) return null;

  return {
    name: destination.name,
    params: {
      ...toGa4Params(sanitizeAnalyticsProperties(properties ?? {})),
      // A rollup's `feature` / `action` override anything the call site sent
      // under those names, so GA4's dimension stays the catalogue's.
      ...destination.params,
      ...analyticsPageContext(window.location.href, document.referrer),
      // Last, so it wins. Several existing call sites pass `surface: 'web'` to
      // distinguish web from native inside PostHog, and letting that through
      // would merge in-app conversions into the marketing site's surface.
      ...analyticsTrafficProperties(document.cookie),
      surface: GA4_SURFACE,
    },
  };
}

/** Shapes a screen view as a GA4 `page_view`, or `null` when GA4 is off. */
export function buildGa4ScreenView(
  pathname: string,
  properties?: Record<string, unknown> | null,
): Ga4QueuedEvent | null {
  if (!isGa4Enabled() || isPrivateAnalyticsPath(pathname)) return null;
  const safeUrl = analyticsPageUrl(window.location.href);
  if (!safeUrl) return null;

  return {
    name: "page_view",
    params: {
      ...analyticsPageContext(safeUrl, document.referrer),
      page_location: safeUrl,
      page_path: new URL(safeUrl).pathname,
      page_title: "Tabbio app",
      ...toGa4Params(sanitizeAnalyticsProperties(properties ?? {})),
      ...analyticsTrafficProperties(document.cookie),
      surface: GA4_SURFACE,
    },
  };
}

/**
 * Hands one shaped event to gtag. Initializes the tag on first use, so a
 * document that never reports anything never loads it.
 */
export function sendGa4Event(item: Ga4QueuedEvent) {
  if (!isGa4Enabled()) return;
  ensureGa4Initialized();
  // Two pushes: the event, then a clear of the parameters it set. GTM merges
  // pushes into a persistent data model, and without the clear a click's
  // `cta_id` would still be there when the next `page_view` fires.
  const [eventPush, clearPush] = buildDataLayerPushes(item.name, item.params);
  const layer = dataLayer();
  layer.push(eventPush);
  layer.push(clearPush);
}

/**
 * Attaches the signed-in user to subsequent events.
 *
 * `user_id` is what lets GA4 join a pre-signup marketing session to the
 * post-signup app session for the same person, so that an acquisition report
 * counts one user rather than two. It is the app's own opaque id, never an
 * email, name or anything else Google's terms forbid sending.
 */
export function identifyGa4User(params: {
  userId: string;
  role?: string | null;
  locale?: string | null;
}) {
  if (!isGa4Enabled()) return;
  ensureGa4Initialized();

  // Persistent on purpose (no clear push): the container's GA4 tags read
  // `user_id`, `user_role` and `locale` from the data model on every hit.
  dataLayer().push(
    buildUserPushes({
      userId: params.userId,
      role: params.role,
      locale: params.locale,
    }),
  );
}

/** Clears the user association on sign-out so the next session is not merged. */
export function resetGa4User() {
  // No `ensureGa4Initialized` here: sign-out runs on every anonymous load, and
  // loading the tag for a visitor who has not yet reached a tracked screen
  // would mean a request to Google on documents that report nothing.
  if (!isGa4Enabled() || !initialized) return;
  dataLayer().push(buildUserPushes({ userId: null }));
}
